import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Map, { Marker } from "react-map-gl/mapbox";
import { mapsUrl } from "../lib/mapsLink";
import { usePrintHold } from "../lib/printReady";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const RED = "#ef4444";
const TEAL = "#0f766e";

// A map that answers one question: where are the competitors, exactly?
//
// The overview map has to hold every ecosystem category across the whole scan
// radius, so at 5 km the competitors collapse into a dot in the middle. This one
// fits to the competitors alone. The ones the list ranks carry their list number,
// so a row and a pin point at each other; the rest stay as plain dots, because
// how crowded the area is matters even when a rival is not in the top eight.
//
// `staticImage` (print): after the tiles paint, the map is swapped for its own
// snapshot and the print page is released (lib/printReady.jsx).
export default function CompetitorMap({
  center, competitors = [], numbered = 8, height = 300, captureRef, label = "Kompetitor", staticImage = false,
}) {
  const mapRef = useRef(null);
  const [active, setActive] = useState(null);
  const [snap, setSnap] = useState(null);
  const releaseHold = usePrintHold(staticImage);

  const cLat = center?.lat, cLng = center?.lng;
  const hasCenter = Number.isFinite(cLat) && Number.isFinite(cLng);
  const pts = useMemo(
    () => (competitors || []).filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lng)),
    [competitors],
  );

  const fit = useCallback(() => {
    const m = mapRef.current?.getMap?.();
    const all = [...(hasCenter ? [{ lat: cLat, lng: cLng }] : []), ...pts];
    if (!m || all.length < 2) return;
    const lats = all.map((p) => p.lat), lngs = all.map((p) => p.lng);
    m.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 44, maxZoom: 17, duration: 0 },
    );
  }, [pts, hasCenter, cLat, cLng]);

  // Refit whenever the competitor set changes (radius switch, another report).
  useEffect(() => { fit(); }, [fit]);

  // Snapshot for the PDF: the basemap canvas plus markers redrawn via map.project,
  // so the export costs no extra Mapbox call and matches what is on screen.
  const capture = useCallback(() => {
    const m = mapRef.current?.getMap?.();
    if (!m) return null;
    try {
      const src = m.getCanvas();
      const w = src.width, h = src.height;
      if (!w || !h) return null;
      const off = document.createElement("canvas");
      off.width = w; off.height = h;
      const ctx = off.getContext("2d");
      ctx.drawImage(src, 0, 0);
      const dpr = w / (m.getContainer().clientWidth || w);
      const disc = (lng, lat, color, r) => {
        const pt = m.project([lng, lat]);
        ctx.beginPath();
        ctx.arc(pt.x * dpr, pt.y * dpr, r * dpr, 0, 2 * Math.PI);
        ctx.fillStyle = color; ctx.fill();
        ctx.lineWidth = 1.5 * dpr; ctx.strokeStyle = "#fff"; ctx.stroke();
        return pt;
      };
      pts.forEach((c, i) => {
        const ranked = i < numbered;
        const pt = disc(c.lng, c.lat, RED, ranked ? 9 : 4);
        if (!ranked) return;
        ctx.fillStyle = "#fff";
        ctx.font = `bold ${10 * dpr}px system-ui, sans-serif`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(String(i + 1), pt.x * dpr, pt.y * dpr + 0.5 * dpr);
      });
      if (hasCenter) disc(cLng, cLat, TEAL, 8);
      return { dataUrl: off.toDataURL("image/png"), w, h };
    } catch {
      return null;
    }
  }, [pts, numbered, hasCenter, cLat, cLng]);

  useEffect(() => {
    if (!captureRef) return undefined;
    captureRef.current = capture;
    return () => { captureRef.current = null; };
  }, [captureRef, capture]);

  // Snapshot once the fitted view has painted; a failed snapshot keeps the live map.
  const onLoad = useCallback(() => {
    const m = mapRef.current?.getMap?.();
    if (staticImage && m) m.resize();   // the canvas may predate a late stylesheet
    fit();
    if (!staticImage) return;
    if (!m) { releaseHold(); return; }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true; clearTimeout(t); m.off("idle", finish);
      setSnap(capture());
      releaseHold();
    };
    const t = setTimeout(finish, 4000);
    m.once("idle", finish);
  }, [fit, staticImage, capture, releaseHold]);

  if (!MAPBOX_TOKEN || !pts.length || !hasCenter) return null;

  return (
    <div>
      <div className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700" style={{ height }}>
        {snap ? (
          <img src={snap.dataUrl} alt={`Peta ${label.toLowerCase()}`} className="w-full h-full object-cover" />
        ) : (
        <Map
          ref={mapRef}
          onLoad={onLoad}
          onError={() => { if (staticImage) releaseHold(); }}
          initialViewState={{ longitude: cLng, latitude: cLat, zoom: 15 }}
          mapStyle="mapbox://styles/mapbox/light-v11"
          mapboxAccessToken={MAPBOX_TOKEN}
          attributionControl={false}
          preserveDrawingBuffer
        >
          <Marker longitude={cLng} latitude={cLat}>
            <div className="w-4 h-4 rounded-full border-[2.5px] border-white shadow-lg" style={{ background: TEAL }} title="Titik Anda" />
          </Marker>
          {pts.map((c, i) => {
            const ranked = i < numbered;
            const href = mapsUrl(c);
            const pin = ranked ? (
              <div
                className="w-[18px] h-[18px] rounded-full border-2 border-white shadow flex items-center justify-center text-[9px] font-extrabold text-white cursor-pointer hover:scale-110 transition-transform"
                style={{ background: RED }}
              >{i + 1}</div>
            ) : (
              <div
                className="w-2.5 h-2.5 rounded-full border border-white shadow cursor-pointer hover:scale-125 transition-transform"
                style={{ background: RED, opacity: 0.75 }}
              />
            );
            return (
              <Marker key={c.place_id || `${c.lat},${c.lng},${i}`} longitude={c.lng} latitude={c.lat}>
                <div
                  onMouseEnter={() => setActive({ ...c, n: ranked ? i + 1 : null })}
                  onMouseLeave={() => setActive(null)}
                  onClick={() => href && window.open(href, "_blank", "noopener,noreferrer")}
                  title={c.name || label}
                >{pin}</div>
              </Marker>
            );
          })}
        </Map>
        )}
        {active && (
          <div className="absolute top-3 left-3 rounded-lg bg-white/95 backdrop-blur px-3 py-2 shadow-lg border border-slate-200 max-w-[240px]">
            <p className="text-[9px] font-bold uppercase tracking-wide" style={{ color: RED }}>
              {active.n ? `${label} #${active.n}` : label}
            </p>
            <p className="text-xs font-semibold text-slate-800 truncate">{active.name}</p>
            {(active.rating != null || active.distance_label) && (
              <p className="text-[10px] text-slate-400">
                {active.rating != null ? `${active.rating}★` : ""}
                {active.rating != null && active.distance_label ? " · " : ""}
                {active.distance_label || ""}
              </p>
            )}
          </div>
        )}
      </div>
      <p className="mt-2 text-[10px] text-slate-400">
        Nomor mengikuti urutan daftar. Titik kecil adalah {label.toLowerCase()} lain di sekitar. Klik pin untuk membukanya di Google Maps.
      </p>
    </div>
  );
}
